/** PHI / HIPAA warning shown to field agents on opening each different client per session. */
import { ShieldAlert, WifiOff, Eye, X } from "lucide-react";

export default function PhiWarningModal({ clientName, onAcknowledge, onCancel }) {
  return (
    <div
      data-testid="phi-warning-modal"
      className="fixed inset-0 z-[90] bg-[#15233B]/70 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 bg-[#b91c1c] text-white flex items-center gap-3">
          <ShieldAlert className="h-6 w-6 shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/85">
              Protected Health Information
            </div>
            <h2 className="text-lg font-extrabold tracking-tight">HIPAA Confidentiality Notice</h2>
          </div>
          <button
            onClick={onCancel}
            data-testid="phi-modal-close"
            className="p-1.5 rounded hover:bg-white/15"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-[#15233B]">
            You are about to view records for{" "}
            <b className="text-[#15233B]">{clientName || "this client"}</b>. The
            information you are about to view contains{" "}
            <b>Protected Health Information (PHI)</b> and may include data
            covered by <b>HIPAA</b>.
          </p>

          <div className="bg-[#FFF9C4] border border-[#FBBF24] rounded-md p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/70">
              Required precautions before continuing
            </div>
            <ul className="space-y-2 text-sm text-[#15233B]">
              <li className="flex items-start gap-2">
                <WifiOff className="h-4 w-4 text-[#b91c1c] shrink-0 mt-0.5" />
                <span>
                  <b>Do not use public Wi-Fi.</b> Connect through a private,
                  trusted network or a secured mobile hotspot.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Eye className="h-4 w-4 text-[#b91c1c] shrink-0 mt-0.5" />
                <span>
                  <b>Use a privacy screen</b> on your device. Ensure no one
                  behind, beside, or above you can see your screen.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-[#b91c1c] shrink-0 mt-0.5" />
                <span>
                  Lock your device when stepping away. Never share or screenshot
                  client information.
                </span>
              </li>
            </ul>
          </div>

          <p className="text-xs text-[#15233B]/70">
            By acknowledging, you confirm you have taken the precautions above
            and will handle this information in accordance with Avida Personal
            Care policy and federal HIPAA regulations.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-[#15233B]/10 bg-[#F2F2F2] flex items-center gap-2">
          <button
            onClick={onCancel}
            data-testid="phi-cancel"
            className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5"
          >
            Cancel
          </button>
          <button
            onClick={onAcknowledge}
            data-testid="phi-acknowledge"
            autoFocus
            className="ml-auto px-5 py-2 rounded bg-[#15233B] hover:bg-[#1E73C9] text-white text-sm font-extrabold"
          >
            I acknowledge — view client
          </button>
        </div>
      </div>
    </div>
  );
}
